import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';

import { AnimatePresence, motion } from 'framer-motion';
import { ShieldCheck } from 'lucide-react';

// Component Imports
import Navbar from './components/Navbar';
import ProductCard from './components/ProductCard';
import ProductModal from './components/ProductModal';
import CartDrawer from './components/CartDrawer';
import AuthModal from './components/AuthModal';
import SkeletonCard from './components/SkeletonCard';
import PromoSidebar from './components/PromoSidebar';
import SearchBar from './components/SearchBar';
import FloatingActions from './components/FloatingActions';

// Admin Imports
import AdminDashboard from './components/admin/AdminDashboard';

// Context Import
import { AppContext } from './context/AppContext';

// Compulsory Environment Variable (Strictly read from .env)
const envApiUrl = process.env.REACT_APP_API_URL;

if (!envApiUrl || envApiUrl === 'undefined') {
    console.error("FATAL: REACT_APP_API_URL is missing in your .env file! Please define REACT_APP_API_URL in .env and restart the dev server.");
}

// Format URL strictly from .env (strips trailing slash if present)
const API_URL = envApiUrl ? (envApiUrl.endsWith('/') ? envApiUrl.slice(0, -1) : envApiUrl) : '';

function App() {
    const { user, darkMode, isAuthOpen, setIsAuthOpen } = useContext(AppContext);

    // 1. Local State
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [search, setSearch] = useState('');
    
    // 2. View State: 'store' or 'admin'
    const [currentView, setCurrentView] = useState('store');

    // 3. Fetch Products
    useEffect(() => {
        const fetchProducts = async () => {
            if (!API_URL) {
                console.error("Fetch aborted: REACT_APP_API_URL is not defined in .env");
                setLoading(false);
                return;
            }

            try {
                const res = await axios.get(`${API_URL}/api/products`);
                setProducts(res.data || []);
                setTimeout(() => setLoading(false), 1200);
            } catch (err) {
                console.error("Backend Connection Error:", err);
                setLoading(false);
            }
        };
        fetchProducts();
    }, []);

    // 4. Search & Filter Logic
    const filteredProducts = products.filter(p => 
        p.model_name?.toLowerCase().includes(search.toLowerCase()) ||
        p.Category?.name?.toLowerCase().includes(search.toLowerCase()) ||
        p.Brand?.name?.toLowerCase().includes(search.toLowerCase())
    );

    // --- RENDER LOGIC ---

    // If Admin mode is active, render the Dashboard instead of the Store
    if (currentView === 'admin' && user?.role === 'admin') {
        return <AdminDashboard closeAdmin={() => setCurrentView('store')} />;
    }

    return (
        <div className={darkMode ? 'dark' : ''}>
            <Navbar onAuthClick={() => setIsAuthOpen(true)} />
            
            <main className="max-w-[1800px] mx-auto px-4 md:px-8 py-2 md:py-4">
                
                <AnimatePresence>
                    {user && (
                        <motion.div 
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            className="mb-4 p-6 rounded-5xl bg-gradient-to-br from-red-600/10 via-green-500/5 to-transparent border border-white/10 backdrop-blur-xl shadow-2xl"
                        >
                            <div className="flex justify-between items-center">
                                <h2 className="text-2xl md:text-3xl font-black tracking-tighter">
                                    Jambo, <span className="text-red-600">{user.name?.split(' ')[0]}</span>! 
                                    <span className="block text-sm font-medium opacity-50 mt-2 tracking-normal">
                                        Your personal high-speed tech hub is ready.
                                    </span>
                                </h2>
                                
                                {/* Secret Admin Toggle: Only visible to Admins */}
                                {user.role === 'admin' && (
                                    <button 
                                        onClick={() => setCurrentView('admin')}
                                        className="bg-red-600/20 hover:bg-red-600 text-red-500 hover:text-white p-4 rounded-2xl transition-all border border-red-600/30 flex items-center gap-3 font-bold group"
                                    >
                                        <ShieldCheck size={20} className="group-hover:scale-110 transition-transform" />
                                        ADMIN PANEL
                                    </button>
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                <div className="flex flex-col xl:flex-row gap-6">
                    <div className="flex-1 order-2 xl:order-1">
                        <SearchBar search={search} setSearch={setSearch} />
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-8 mt-6">
                            {loading ? (
                                [...Array(8)].map((_, i) => <SkeletonCard key={i} />)
                            ) : filteredProducts.length > 0 ? (
                                filteredProducts.map((p, i) => (
                                    <ProductCard 
                                        key={p.id || p._id || i} 
                                        product={p} 
                                        index={i} 
                                        onClick={setSelectedProduct} 
                                    />
                                ))
                            ) : (
                                <div className="col-span-full text-center py-32 opacity-30">
                                    <p className="text-3xl font-black italic">No tech matches your search.</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {!loading && (
                        <div className="order-1 xl:order-2">
                            <PromoSidebar products={products} onProductClick={setSelectedProduct} />
                        </div>
                    )}
                </div>
            </main>

            {/* Overlays */}
            <CartDrawer />
            <AuthModal isOpen={isAuthOpen} onClose={() => setIsAuthOpen(false)} />

            <AnimatePresence>
                {selectedProduct && (
                    <ProductModal 
                        product={selectedProduct} 
                        onClose={() => setSelectedProduct(null)} 
                        openAuth={() => setIsAuthOpen(false)}
                    />
                )}
            </AnimatePresence>

            <FloatingActions />

            <footer className="mt-20 py-12 border-t border-white/5 text-center">
                <div className="opacity-30 text-xs font-mono tracking-widest uppercase">
                    Swift Systems Architecture // Nairobi 2026
                </div>
            </footer>
        </div>
    );
}

export default App;